import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Progress } from "./components/ui/progress";
import { ScrollArea } from "./components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { 
  Menu, X, MessageCircle, Send, Globe, Code, Smartphone, Search, 
  TrendingUp, Users, Mail, Phone, MapPin, ArrowRight, Check, 
  Star, Zap, Shield, Target, BarChart3, Palette, FileText, 
  Instagram, Facebook, Linkedin, ChevronRight, Loader2,
  AlertCircle, CheckCircle, XCircle, LogOut, Eye, EyeOff,
  Download, Upload, MessageSquare, FolderOpen, Clock, User,
  Twitter, Youtube, Share2, Lock, Wrench, ExternalLink, Calendar,
  ChevronLeft, PhoneCall
} from "lucide-react";
import { Helmet } from "react-helmet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_67824de4-6849-462e-8647-eb54846d39f0/artifacts/pdo5cdn9_simo%20marketing.png";

// ==================== SCROLL TO TOP ====================
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// ==================== SEO HEAD ====================
const SEOHead = ({ title = "Agenzia Marketing Digitale", description = "SIMO Marketing" }) => (
  <Helmet>
    <title>{`${title} | SIMO Marketing`}</title>
    <meta name="description" content={description} />
    <meta name="keywords" content="marketing digitale, sviluppo web, app mobile, SEO, social media, Caltanissetta, Italia, agenzia marketing" />
    <meta property="og:title" content={`${title} | SIMO Marketing`} />
    <meta property="og:description" content={description} />
    <meta property="og:image" content={LOGO_URL} />
    <meta property="og:type" content="website" />
    <meta property="og:locale" content="it_IT" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={`${title} | SIMO Marketing`} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={LOGO_URL} />
    <link rel="canonical" href={window.location.href} />
  </Helmet>
);

// ==================== PARTICLES BACKGROUND ====================
const ParticlesBackground = () => {
  const [init, setInit] = useState(false);
  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => setInit(true));
  }, []);

  const options = {
    background: { color: { value: "transparent" } },
    fpsLimit: 60,
    particles: {
      color: { value: "#00f0ff" },
      links: { color: "#00f0ff", distance: 150, enable: true, opacity: 0.3, width: 1 },
      move: { enable: true, speed: 1, direction: "none", outModes: { default: "bounce" } },
      number: { density: { enable: true, area: 800 }, value: 60 },
      opacity: { value: 0.5 },
      shape: { type: "circle" },
      size: { value: { min: 1, max: 3 } },
    },
    detectRetina: true,
  };

  if (!init) return null;
  return <Particles id="tsparticles" options={options} />;
};

// ==================== NAVBAR ====================
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/chi-siamo", label: "Chi Siamo" },
    { path: "/servizi", label: "Servizi" },
    { path: "/portfolio", label: "Portfolio" },
    { path: "/tools", label: "Tools Gratuiti" },
    { path: "/consulenza", label: "Consulenza" },
    { path: "/contatti", label: "Contatti" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-[#050a14] shadow-lg shadow-black/50 border-b border-[#00f0ff]/20" 
          : "bg-[#050a14]/95 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="flex items-center justify-between h-20 md:h-28">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="SIMO Marketing" className="h-12 w-12 sm:h-16 sm:w-16 md:h-24 md:w-24 object-contain" />
          </Link>

          <div className="hidden lg:flex items-center gap-5 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace("/", "") || "home"}`}
                className={`text-base xl:text-lg font-semibold transition-colors duration-300 whitespace-nowrap ${
                  location.pathname === link.path ? "text-[#00f0ff]" : "text-white/80 hover:text-[#00f0ff]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/accesso">
              <Button size="lg" className="bg-[#00f0ff] text-black font-bold hover:bg-[#00f0ff]/80 text-base px-6" data-testid="login-btn">
                Accedi
              </Button>
            </Link>
          </div>

          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="lg:hidden text-white p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" 
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-[#050a14] border-t border-white/10"
            >
              <div className="py-6 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-4 px-4 text-lg font-semibold rounded-lg transition-colors ${
                      location.pathname === link.path 
                        ? "text-[#00f0ff] bg-[#00f0ff]/10" 
                        : "text-white/80 hover:text-[#00f0ff] hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 px-4">
                  <Link to="/accesso" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-[#00f0ff] text-black font-bold text-lg py-6 hover:bg-[#00f0ff]/80">
                      Accedi
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Social Links
const SOCIAL_LINKS = {
  linkedin: "https://www.linkedin.com/company/simomarketing/",
  facebook: "https://www.facebook.com/share/1HJyVgM4jP/",
  instagram: "https://www.instagram.com/simo_marketing_ita?igsh=cXVmcGJ4N3lpbG9v"
};

// ==================== FOOTER ====================
const Footer = () => (
  <footer className="relative z-10 bg-[#0f172a] border-t border-white/10 py-16">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div>
          <img src={LOGO_URL} alt="SIMO Marketing" className="h-16 mb-6" />
          <p className="text-white/60 text-sm">
            Trasformiamo le tue idee in realtà digitali. Dal 2022 al servizio delle aziende italiane e internazionali.
          </p>
        </div>
        <div>
          <h4 className="font-bold mb-4">Servizi</h4>
          <ul className="space-y-2 text-white/60 text-sm">
            <li><Link to="/servizi" className="hover:text-[#00f0ff] transition-colors">Landing Page da €200</Link></li>
            <li><Link to="/servizi" className="hover:text-[#00f0ff] transition-colors">Sviluppo Web</Link></li>
            <li><Link to="/servizi" className="hover:text-[#00f0ff] transition-colors">App Mobile</Link></li>
            <li><Link to="/servizi" className="hover:text-[#00f0ff] transition-colors">SEO & Marketing</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold mb-4">Link Utili</h4>
          <ul className="space-y-2 text-white/60 text-sm">
            <li><Link to="/chi-siamo" className="hover:text-[#00f0ff] transition-colors">Chi Siamo</Link></li>
            <li><Link to="/portfolio" className="hover:text-[#00f0ff] transition-colors">Portfolio</Link></li>
            <li><Link to="/seo-tool" className="hover:text-[#00f0ff] transition-colors">SEO Tool Gratuito</Link></li>
            <li><Link to="/consulenza" className="hover:text-[#00f0ff] transition-colors">Consulenza Gratuita</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold mb-4">Contatti</h4>
          <ul className="space-y-3 text-white/60 text-sm">
            <li className="flex items-center gap-2">
              <MapPin size={16} className="text-[#00f0ff]" />
              Viale Regina Margherita, Caltanissetta
            </li>
            <li className="flex items-center gap-2">
              <Phone size={16} className="text-[#00f0ff]" />
              <a href="tel:+393513449302" className="hover:text-[#00f0ff]">+39 351 344 9302</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail size={16} className="text-[#00f0ff]" />
              <a href="mailto:info@simomarketing.it" className="hover:text-[#00f0ff]">info@simomarketing.it</a>
            </li>
          </ul>
          <div className="flex gap-4 mt-6">
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#00f0ff] transition-colors"><Instagram size={20} /></a>
            <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#00f0ff] transition-colors"><Facebook size={20} /></a>
            <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#00f0ff] transition-colors"><Linkedin size={20} /></a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/40 text-sm">
        © 2026 SIMO Marketing. Tutti i diritti riservati.
      </div>
    </div>
  </footer>
);

// ==================== CHATBOT ====================
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Ciao! Sono l'assistente di SIMO Marketing. Come posso aiutarti? 🚀" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, { message: userMessage, session_id: sessionId });
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: "bot", text: response.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "Mi scuso, c'è stato un problema. Contattaci al 351 344 9302." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 z-40 w-16 h-16 bg-[#00f0ff] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(0,240,255,0.5)] hover:scale-110 transition-transform"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chat-button"
      >
        <MessageCircle size={28} className="text-black" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-44 right-6 z-40 w-[380px] max-w-[calc(100vw-48px)] bg-[#0f172a]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
            data-testid="chat-window"
          >
            <div className="bg-gradient-to-r from-[#1a365d] to-[#0f172a] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#00f0ff]/20 flex items-center justify-center">
                  <MessageCircle size={20} className="text-[#00f0ff]" />
                </div>
                <div>
                  <h4 className="font-bold text-sm">Assistente SIMO</h4>
                  <span className="text-xs text-green-400 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span> Online
                  </span>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/60 hover:text-white"><X size={20} /></button>
            </div>

            <ScrollArea className="h-80 p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm ${
                      msg.role === "user" ? "bg-[#1a365d] text-white rounded-br-none" : "bg-white/10 text-white rounded-bl-none"
                    }`}>
                      {msg.text}
                    </div>
                  </motion.div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white/10 px-4 py-3 rounded-2xl rounded-bl-none flex gap-1">
                      <span className="w-2 h-2 bg-[#00f0ff] rounded-full typing-dot"></span>
                      <span className="w-2 h-2 bg-[#00f0ff] rounded-full typing-dot"></span>
                      <span className="w-2 h-2 bg-[#00f0ff] rounded-full typing-dot"></span>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Scrivi un messaggio..." className="flex-1 bg-white/5 border-white/10" data-testid="chat-input" />
                <Button onClick={sendMessage} disabled={loading || !input.trim()}
                  className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/80" data-testid="chat-send">
                  <Send size={18} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== ROTATING HERO TITLE ====================
const RotatingHeroTitle = () => {
  const words = [
    "digitali",
    "social",
    "web",
    "app",
    "e-commerce",
    "SEO",
    "marketing",
    "sicuri"
  ];
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % words.length);
        setIsAnimating(false);
      }, 500);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <motion.h1 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.6, delay: 0.2 }}
      className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight"
    >
      <span className="text-white">Il partner ideale per i tuoi</span>
      <br />
      <span className="text-white">progetti </span>
      <span 
        className={`text-[#00f0ff] inline-block transition-all duration-500 ${
          isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
        }`}
      >
        {words[currentIndex]}
      </span>
    </motion.h1>
  );
};

// ==================== HOME PAGE ====================
const HomePage = () => {
  const services = [
    { icon: Globe, title: "Sviluppo Web", desc: "Siti moderni da €800", span: "col-span-2" },
    { icon: Smartphone, title: "App Mobile", desc: "iOS & Android da €5.000", span: "" },
    { icon: FileText, title: "Landing Page", desc: "Da €200 a vita!", span: "", highlight: true },
    { icon: Search, title: "SEO", desc: "Da €300/mese", span: "" },
    { icon: Shield, title: "Cyber Security", desc: "Protezione completa", span: "", highlight: true },
    { icon: TrendingUp, title: "Advertising", desc: "Google & Meta Ads", span: "" },
  ];

  const reviews = [
    { name: "Marco Rossi", company: "Tech Solutions Srl", rating: 5, text: "Hanno trasformato la nostra presenza online. Risultati eccezionali!" },
    { name: "Laura Bianchi", company: "Boutique Elena", rating: 5, text: "Professionali e creativi. Il nostro e-commerce ha triplicato le vendite." },
    { name: "Giovanni Verdi", company: "Studio Legale Verdi", rating: 5, text: "Sito elegante e funzionale. Altamente raccomandati." },
  ];

  return (
    <div className="relative min-h-screen" data-testid="home-page">
      <SEOHead 
        title="Agenzia Marketing Digitale"
        description="SIMO Marketing: agenzia di marketing digitale a Caltanissetta. Sviluppo siti web, app mobile, SEO, social media marketing. Landing page da €200. Consulenza gratuita!"
      />

      {/* Hero Section - Clean Minimal Style */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Clean gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#050a14] via-[#0a1628] to-[#050a14]"></div>
        
        {/* Subtle animated gradient orbs - hidden on mobile */}
        <div className="hidden md:block absolute top-1/4 left-1/4 w-96 h-96 bg-[#00f0ff]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="hidden md:block absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#00f0ff]/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Main Heading with Rotating Text */}
            <RotatingHeroTitle />
            
            {/* Subtitle - Simple and Clear */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed"
            >
              Sviluppiamo strategie di marketing e offriamo competenze mirate alla crescita 
              del tuo business con strumenti e metodi innovativi.
            </motion.p>
            
            {/* Single CTA Button - Prominent */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.6 }}
              className="pt-4"
            >
              <Link to="/consulenza">
                <Button 
                  size="lg" 
                  className="bg-white text-[#050a14] hover:bg-white/90 px-12 py-7 text-lg font-semibold rounded-full shadow-lg shadow-white/10 hover:shadow-white/20 transition-all duration-300" 
                  data-testid="cta-consulenza"
                >
                  RICHIEDI UN PREVENTIVO
                </Button>
              </Link>
            </motion.div>
            
            {/* Scroll indicator */}
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              transition={{ duration: 0.6, delay: 1 }}
              className="pt-12"
            >
              <motion.div 
                animate={{ y: [0, 10, 0] }} 
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-6 h-10 rounded-full border-2 border-white/20 mx-auto flex items-start justify-center p-2"
              >
                <div className="w-1.5 h-3 bg-[#00f0ff]/60 rounded-full"></div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">I Nostri <span className="text-[#00f0ff]">Servizi</span></h2>
            <p className="text-white/60">Soluzioni complete per la tua crescita digitale</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {services.map((service, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link to="/servizi">
                  <div className={`service-card glass-card p-6 rounded-xl cursor-pointer group h-full ${service.highlight ? 'border-[#00f0ff]/50 bg-[#00f0ff]/5' : ''}`}>
                    <service.icon className="text-[#00f0ff] mb-4 group-hover:scale-110 transition-transform" size={32} />
                    <h3 className="font-bold text-lg mb-2">{service.title}</h3>
                    <p className="text-white/60 text-sm">{service.desc}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/servizi"><Button variant="outline" className="btn-secondary">Tutti i servizi e prezzi <ChevronRight size={18} /></Button></Link>
          </div>
        </div>
      </section>

      <section className="relative z-10 py-24 bg-[#0f172a]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "50+", label: "Progetti Completati" },
              { value: "30+", label: "Clienti Soddisfatti" },
              { value: "100%", label: "Passione" },
              { value: "24/7", label: "Supporto" },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-[#00f0ff] mb-2">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Cosa Dicono i <span className="text-[#00f0ff]">Clienti</span></h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((review, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className="glass-card h-full">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(review.rating)].map((_, j) => <Star key={j} size={16} className="fill-yellow-400 text-yellow-400" />)}
                    </div>
                    <p className="text-white/80 mb-4 italic">"{review.text}"</p>
                    <div>
                      <div className="font-bold">{review.name}</div>
                      <div className="text-sm text-white/60">{review.company}</div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card p-12 rounded-2xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Pronto a far crescere il tuo <span className="text-[#00f0ff]">business</span>?
            </h2>
            <p className="text-white/60 mb-8">Richiedi una consulenza gratuita e scopri come possiamo aiutarti</p>
            <Link to="/consulenza"><Button size="lg" className="btn-primary px-8">Inizia Ora <ArrowRight className="ml-2" size={20} /></Button></Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// ==================== SERVIZI PAGE (CON PREZZI) ====================
const ServiziPage = () => {
  const services = [
    {
      icon: FileText, title: "Landing Page", price: "€200", priceNote: "una tantum, a vita!",
      desc: "Pagina di atterraggio ottimizzata per la conversione", highlight: true,
      features: ["Design personalizzato", "Mobile responsive", "SEO ottimizzata", "Form contatto", "Hosting 1° anno incluso", "Nessuna spesa nascosta"]
    },
    {
      icon: Globe, title: "Sito Vetrina", price: "da €800", priceNote: "5-10 pagine",
      desc: "Sito web professionale per presentare la tua attività",
      features: ["Design moderno", "5-10 pagine", "SEO base", "Form contatto", "CMS facile da gestire"]
    },
    {
      icon: Globe, title: "Sito Aziendale", price: "da €2.000", priceNote: "completo",
      desc: "Sito completo con blog, newsletter e funzionalità avanzate",
      features: ["Design premium", "Blog integrato", "Newsletter", "SEO avanzata", "Area riservata", "Multi-lingua opzionale"]
    },
    {
      icon: Code, title: "E-commerce", price: "da €3.000", priceNote: "",
      desc: "Negozio online completo con gestione ordini e pagamenti",
      features: ["Catalogo prodotti", "Carrello e checkout", "Pagamenti sicuri", "Gestione ordini", "Spedizioni integrate"]
    },
    {
      icon: Smartphone, title: "App Mobile", price: "da €5.000", priceNote: "iOS & Android",
      desc: "Applicazione mobile nativa o cross-platform",
      features: ["UI/UX design", "iOS & Android", "Backend incluso", "Pubblicazione store", "Manutenzione 6 mesi"]
    },
    {
      icon: Wrench, title: "Manutenzione Siti & App", price: "da €50", priceNote: "/mese",
      desc: "Assistenza tecnica e aggiornamenti per siti e applicazioni",
      features: ["Codice personalizzato", "WordPress", "Joomla, Drupal, Prestashop", "Aggiornamenti sicurezza", "Backup automatici", "Supporto prioritario"]
    },
    {
      icon: Shield, title: "Cyber Security", price: "da €500", priceNote: "/mese", highlight: true,
      desc: "Protezione completa per aziende e privati contro minacce informatiche",
      features: ["Audit sicurezza", "Penetration testing", "Monitoraggio 24/7", "Protezione DDoS", "Formazione dipendenti", "Incident response"]
    },
    {
      icon: Search, title: "SEO", price: "da €300", priceNote: "/mese",
      desc: "Ottimizzazione per motori di ricerca",
      features: ["Audit SEO completo", "Keyword research", "Ottimizzazione on-page", "Link building", "Report mensili"]
    },
    {
      icon: TrendingUp, title: "Google Ads", price: "da €300", priceNote: "/mese + budget",
      desc: "Campagne pubblicitarie su Google",
      features: ["Setup campagne", "Ricerca keyword", "Ottimizzazione CPC", "Remarketing", "Report settimanali"]
    },
    {
      icon: Instagram, title: "Social Media", price: "da €400", priceNote: "/mese",
      desc: "Gestione professionale dei tuoi canali social",
      features: ["Piano editoriale", "12 post/mese", "Stories", "Community management", "Report mensili"]
    },
    {
      icon: Palette, title: "Branding / Logo", price: "da €200", priceNote: "",
      desc: "Identità visiva per il tuo brand",
      features: ["3 proposte logo", "File vettoriali", "Palette colori", "Typography", "Brand guidelines base"]
    },
    {
      icon: Mail, title: "Email Marketing", price: "da €200", priceNote: "/mese",
      desc: "Campagne email per fidelizzare i clienti",
      features: ["Template design", "4 newsletter/mese", "Automazioni base", "Segmentazione", "Analytics"]
    },
  ];

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="servizi-page">
      <SEOHead title="Servizi e Prezzi" description="Scopri tutti i servizi di SIMO Marketing: Landing Page da €200, siti web, app mobile, SEO, social media. Prezzi trasparenti, nessuna spesa nascosta." />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
            <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30">PREZZI TRASPARENTI</Badge>
            <h1 className="text-3xl lg:text-4xl font-bold mb-6">
              I Nostri <span className="text-[#00f0ff]">Servizi</span>
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Nessuna spesa nascosta. Prezzi chiari e competitivi.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Card className={`glass-card h-full ${service.highlight ? 'border-[#00f0ff]/50 relative overflow-hidden' : ''}`}>
                  {service.highlight && (
                    <div className="absolute top-0 right-0 bg-[#00f0ff] text-black text-xs font-bold px-3 py-1">
                      PIÙ RICHIESTO
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${service.highlight ? 'bg-[#00f0ff]/20' : 'bg-white/5'}`}>
                        <service.icon className="text-[#00f0ff]" size={28} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold">{service.title}</h3>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-[#00f0ff]">{service.price}</span>
                            {service.priceNote && <span className="text-xs text-white/60 block">{service.priceNote}</span>}
                          </div>
                        </div>
                        <p className="text-white/60 mb-4 text-sm">{service.desc}</p>
                        <ul className="grid grid-cols-2 gap-2">
                          {service.features.map((feature, j) => (
                            <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                              <Check size={14} className="text-[#00f0ff]" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card p-12 rounded-2xl">
            <h2 className="text-2xl font-bold mb-4">Non sai quale servizio fa per te?</h2>
            <p className="text-white/60 mb-8">Prenota una consulenza gratuita e ti aiuteremo a scegliere</p>
            <Link to="/consulenza"><Button size="lg" className="btn-primary px-8">Consulenza Gratuita <ArrowRight className="ml-2" size={20} /></Button></Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// ==================== SOCIAL TOOL PAGE ====================
const SocialToolPage = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyzeSocial = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/social/analyze`, { url: url.trim() });
      setResult(response.data);
      // Save lead
      if (response.data.score !== undefined) {
        try {
          await axios.post(`${API}/tool-leads`, {
            tool_type: "social",
            url: url.trim(),
            result_score: response.data.score
          });
        } catch { }
      }
    } catch {
      setResult({ error: true, recommendations: ["Errore durante l'analisi"] });
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'instagram': return <Instagram className="text-pink-500" />;
      case 'facebook': return <Facebook className="text-blue-500" />;
      case 'twitter': return <Twitter className="text-sky-400" />;
      case 'linkedin': return <Linkedin className="text-blue-600" />;
      case 'youtube': return <Youtube className="text-red-500" />;
      case 'tiktok': return <Share2 className="text-white" />;
      default: return <Globe className="text-[#00f0ff]" />;
    }
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="social-tool-page">
      <SEOHead title="Analisi Social Media Gratuita" description="Analizza gratuitamente i tuoi profili social. Ottieni consigli per migliorare la tua presenza su Instagram, Facebook, TikTok, LinkedIn e YouTube." />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
            <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30">STRUMENTO GRATUITO</Badge>
            <h1 className="text-3xl lg:text-4xl font-bold mb-6">Analisi <span className="text-[#00f0ff]">Social Media</span></h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-12">
              Inserisci l'URL del tuo profilo social e ricevi consigli per migliorare
            </p>

            <div className="max-w-2xl mx-auto">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Share2 className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                  <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyPress={(e) => e.key === "Enter" && analyzeSocial()}
                    placeholder="https://instagram.com/tuoprofilo" className="pl-12 h-14 text-lg bg-white/5 border-white/10" data-testid="social-url-input" />
                </div>
                <Button onClick={analyzeSocial} disabled={loading || !url.trim()} className="h-14 px-8 btn-primary" data-testid="social-analyze-btn">
                  {loading ? <Loader2 className="animate-spin" size={20} /> : <>Analizza</>}
                </Button>
              </div>
              <p className="text-white/40 text-sm mt-4">Supporta: Instagram, Facebook, TikTok, LinkedIn, YouTube, Twitter/X</p>
            </div>
          </motion.div>
        </div>
      </section>

      {result && !result.error && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="glass-card mb-6">
                <CardContent className="p-8">
                  <div className="flex items-center gap-6 mb-6">
                    <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                      {getPlatformIcon(result.platform)}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold capitalize">{result.platform}</h2>
                      {result.username && <p className="text-white/60">@{result.username}</p>}
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-4xl font-bold text-[#00f0ff]">{result.score}</div>
                      <div className="text-sm text-white/60">Punteggio</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader><CardTitle>Raccomandazioni</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {result.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-3">
                        {rec.startsWith("✅") ? <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" /> :
                         rec.startsWith("⚠️") ? <AlertCircle size={18} className="text-yellow-400 flex-shrink-0 mt-0.5" /> :
                         rec.startsWith("❌") ? <XCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" /> :
                         rec.startsWith("💡") ? <Zap size={18} className="text-[#00f0ff] flex-shrink-0 mt-0.5" /> :
                         <Check size={18} className="text-white/40 flex-shrink-0 mt-0.5" />}
                        <span className="text-white/80">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <div className="mt-8 text-center">
                <p className="text-white/60 mb-4">Vuoi migliorare la tua presenza social?</p>
                <Link to="/consulenza"><Button className="btn-primary">Richiedi Consulenza Social <ArrowRight className="ml-2" size={18} /></Button></Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

// ==================== ACCESS PAGE (SCELTA CLIENTE/ADMIN) ====================
const AccessPage = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen pt-28 md:pt-32 flex items-center justify-center" data-testid="access-page">
      <SEOHead title="Accesso" description="Accedi alla tua area riservata SIMO Marketing" />
      
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
          <img src={LOGO_URL} alt="SIMO Marketing" className="h-20 mx-auto mb-8" />
          <h1 className="text-3xl font-bold mb-4">Scegli il tipo di <span className="text-[#00f0ff]">accesso</span></h1>
          <p className="text-white/60 mb-12">Seleziona la tua area riservata</p>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div whileHover={{ scale: 1.02 }} className="cursor-pointer" onClick={() => navigate('/login-cliente')}>
              <Card className="glass-card h-full hover:border-[#00f0ff]/50 transition-colors">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-[#00f0ff]/10 flex items-center justify-center mx-auto mb-6">
                    <User size={40} className="text-[#00f0ff]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Area Cliente</h3>
                  <p className="text-white/60 text-sm">
                    Accedi per vedere lo stato dei tuoi progetti, scaricare documenti e comunicare con il team
                  </p>
                  <Button className="mt-6 w-full" variant="outline" data-testid="access-client-btn">
                    Accesso Cliente <ArrowRight className="ml-2" size={18} />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div whileHover={{ scale: 1.02 }} className="cursor-pointer" onClick={() => navigate('/login-admin')}>
              <Card className="glass-card h-full hover:border-[#00f0ff]/50 transition-colors">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-[#7000ff]/10 flex items-center justify-center mx-auto mb-6">
                    <Shield size={40} className="text-[#7000ff]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Amministratore</h3>
                  <p className="text-white/60 text-sm">
                    Accesso riservato agli amministratori di sistema con verifica 2FA
                  </p>
                  <Button className="mt-6 w-full" variant="outline" data-testid="access-admin-btn">
                    Accesso Admin <ArrowRight className="ml-2" size={18} />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ==================== CLIENT LOGIN PAGE ====================
const ClientLoginPage = () => {
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", phone: "", company: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegistering ? "/auth/register/client" : "/auth/login/client";
      const response = await axios.post(`${API}${endpoint}`, formData);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("userRole", "client");
      navigate("/dashboard-cliente");
    } catch (error) {
      alert(error.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32 flex items-center justify-center" data-testid="client-login-page">
      <SEOHead title="Accesso Cliente" description="Accedi alla tua area cliente SIMO Marketing" />
      
      <div className="max-w-md w-full mx-auto px-6">
        <Card className="glass-card">
          <CardHeader className="text-center">
            <img src={LOGO_URL} alt="SIMO Marketing" className="h-16 mx-auto mb-4" />
            <CardTitle>{isRegistering ? "Registrazione Cliente" : "Accesso Cliente"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegistering && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome *</label>
                    <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-white/5 border-white/10" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Telefono</label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-white/5 border-white/10" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Azienda</label>
                    <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-white/5 border-white/10" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/5 border-white/10" data-testid="client-email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password *</label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} required value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-white/5 border-white/10 pr-10" data-testid="client-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="client-submit">
                {loading ? <Loader2 className="animate-spin mr-2" /> : null}
                {isRegistering ? "Registrati" : "Accedi"}
              </Button>
              <p className="text-center text-sm text-white/60">
                {isRegistering ? "Hai già un account? " : "Non hai un account? "}
                <button type="button" onClick={() => setIsRegistering(!isRegistering)} className="text-[#00f0ff] hover:underline">
                  {isRegistering ? "Accedi" : "Registrati"}
                </button>
              </p>
            </form>
            <div className="mt-4 text-center">
              <Link to="/accesso" className="text-sm text-white/40 hover:text-white/60">← Torna alla scelta accesso</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== ADMIN LOGIN PAGE (CON 2FA) ====================
const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("credentials"); // credentials | 2fa
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [verificationCode, setVerificationCode] = useState("");

  const handleCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegistering ? "/auth/register/admin" : "/auth/login/admin";
      await axios.post(`${API}${endpoint}`, formData);
      setStep("2fa");
    } catch (error) {
      alert(error.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegistering ? "/auth/verify/admin" : "/auth/verify/login";
      const response = await axios.post(`${API}${endpoint}`, { email: formData.email, code: verificationCode });
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("userRole", "admin");
      navigate("/dashboard-admin");
    } catch (error) {
      alert(error.response?.data?.detail || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32 flex items-center justify-center" data-testid="admin-login-page">
      <SEOHead title="Accesso Amministratore" description="Accesso riservato agli amministratori SIMO Marketing" />
      
      <div className="max-w-md w-full mx-auto px-6">
        <Card className="glass-card">
          <CardHeader className="text-center">
            <img src={LOGO_URL} alt="SIMO Marketing" className="h-16 mx-auto mb-4" />
            <CardTitle>
              {step === "2fa" ? "Verifica 2FA" : isRegistering ? "Registrazione Admin" : "Accesso Admin"}
            </CardTitle>
            {step === "2fa" && (
              <p className="text-white/60 text-sm mt-2">
                Inserisci il codice di verifica
              </p>
            )}
          </CardHeader>
          <CardContent>
            {step === "credentials" ? (
              <form onSubmit={handleCredentials} className="space-y-4">
                {isRegistering && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome *</label>
                    <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-white/5 border-white/10" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-white/5 border-white/10" data-testid="admin-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Password *</label>
                  <div className="relative">
                    <Input type={showPassword ? "text" : "password"} required value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="bg-white/5 border-white/10 pr-10" data-testid="admin-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="admin-submit">
                  {loading ? <Loader2 className="animate-spin mr-2" /> : null}
                  {isRegistering ? "Registrati" : "Accedi"}
                </Button>
                <p className="text-center text-sm text-white/60">
                  {isRegistering ? "Hai già un account? " : "Non hai un account? "}
                  <button type="button" onClick={() => setIsRegistering(!isRegistering)} className="text-[#00f0ff] hover:underline">
                    {isRegistering ? "Accedi" : "Registrati"}
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Codice di Verifica</label>
                  <Input type="text" required maxLength={8} value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="bg-white/5 border-white/10 text-center text-2xl tracking-widest" placeholder="00000000"
                    data-testid="admin-2fa-code" />
                </div>
                <Button type="submit" disabled={loading || verificationCode.length !== 8} className="w-full btn-primary">
                  {loading ? <Loader2 className="animate-spin mr-2" /> : null} Verifica
                </Button>
                <button type="button" onClick={() => setStep("credentials")} className="w-full text-center text-sm text-white/60 hover:text-white">
                  ← Torna indietro
                </button>
              </form>
            )}
            <div className="mt-4 text-center">
              <Link to="/accesso" className="text-sm text-white/40 hover:text-white/60">← Torna alla scelta accesso</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== CLIENT DASHBOARD ====================
const ClientDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");

  const loadData = useCallback(async () => {
    try {
      const [userRes, projectsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setUser(userRes.data);
      setProjects(projectsRes.data);
    } catch {
      localStorage.removeItem("token");
      navigate("/login-cliente");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/login-cliente"); return; }
    loadData();
  }, [token, navigate, loadData]);

  const loadMessages = async (projectId) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(res.data);
    } catch { }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedProject) return;
    try {
      await axios.post(`${API}/projects/${selectedProject.id}/messages`, { project_id: selectedProject.id, content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } });
      setNewMessage("");
      loadMessages(selectedProject.id);
    } catch { }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    navigate("/");
  };

  const getStatusBadge = (status) => {
    const colors = {
      in_attesa: "bg-yellow-500/20 text-yellow-400",
      in_lavorazione: "bg-blue-500/20 text-blue-400",
      revisione: "bg-purple-500/20 text-purple-400",
      completato: "bg-green-500/20 text-green-400"
    };
    const labels = {
      in_attesa: "In Attesa",
      in_lavorazione: "In Lavorazione",
      revisione: "In Revisione",
      completato: "Completato"
    };
    return <Badge className={colors[status] || "bg-white/10"}>{labels[status] || status}</Badge>;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#00f0ff]" size={48} /></div>;

  return (
    <div className="relative min-h-screen pt-28 md:pt-32 pb-12" data-testid="client-dashboard">
      <SEOHead title="Dashboard Cliente" description="La tua area riservata SIMO Marketing" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold">Ciao, <span className="text-[#00f0ff]">{user?.name}</span></h1>
            <p className="text-white/60 text-sm">Benvenuto nella tua area riservata</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="btn-secondary" data-testid="logout-btn">
            <LogOut size={18} className="mr-2" /> Esci
          </Button>
        </div>

        {projects.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <FolderOpen size={48} className="mx-auto mb-4 text-white/40" />
              <h3 className="text-xl font-bold mb-2">Nessun progetto attivo</h3>
              <p className="text-white/60 mb-6">Non hai ancora progetti. Contattaci per iniziare!</p>
              <Link to="/consulenza"><Button className="btn-primary">Richiedi Consulenza</Button></Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <h2 className="text-lg font-bold mb-4">I Tuoi Progetti</h2>
              {projects.map((project) => (
                <Card key={project.id} className={`glass-card cursor-pointer transition-all ${selectedProject?.id === project.id ? 'border-[#00f0ff]/50' : ''}`}
                  onClick={() => { setSelectedProject(project); loadMessages(project.id); }}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold">{project.name}</h3>
                      {getStatusBadge(project.status)}
                    </div>
                    <p className="text-white/60 text-sm mb-3">{project.service_type}</p>
                    <div className="flex items-center gap-2">
                      <Progress value={project.progress} className="flex-1 h-2" />
                      <span className="text-xs text-[#00f0ff]">{project.progress}%</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="lg:col-span-2">
              {selectedProject ? (
                <Card className="glass-card h-full">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>{selectedProject.name}</CardTitle>
                        <p className="text-white/60 text-sm mt-1">{selectedProject.description}</p>
                      </div>
                      {getStatusBadge(selectedProject.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="progress">
                      <TabsList className="mb-4 bg-white/5">
                        <TabsTrigger value="progress">Avanzamento</TabsTrigger>
                        <TabsTrigger value="documents">Documenti ({selectedProject.documents?.length || 0})</TabsTrigger>
                        <TabsTrigger value="messages">Messaggi</TabsTrigger>
                      </TabsList>

                      <TabsContent value="progress">
                        <div className="space-y-6">
                          <div>
                            <div className="flex justify-between mb-2">
                              <span className="text-sm text-white/60">Progresso totale</span>
                              <span className="font-bold text-[#00f0ff]">{selectedProject.progress}%</span>
                            </div>
                            <Progress value={selectedProject.progress} className="h-4" />
                          </div>
                          {selectedProject.notes && (
                            <div className="bg-white/5 rounded-lg p-4">
                              <h4 className="font-bold mb-2 flex items-center gap-2"><Clock size={16} /> Ultime Note</h4>
                              <p className="text-white/70 text-sm">{selectedProject.notes}</p>
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="documents">
                        <div className="space-y-3">
                          {selectedProject.documents?.length === 0 ? (
                            <p className="text-white/60 text-center py-8">Nessun documento caricato</p>
                          ) : (
                            selectedProject.documents?.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between bg-white/5 rounded-lg p-4">
                                <div className="flex items-center gap-3">
                                  <FileText className="text-[#00f0ff]" />
                                  <div>
                                    <p className="font-medium">{doc.original_name}</p>
                                    <p className="text-xs text-white/60">{doc.doc_type} • {new Date(doc.uploaded_at).toLocaleDateString("it-IT")}</p>
                                  </div>
                                </div>
                                <a href={`${API}/documents/${doc.id}/download`} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm"><Download size={16} /></Button>
                                </a>
                              </div>
                            ))
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="messages">
                        <div className="space-y-4">
                          <ScrollArea className="h-64 pr-4">
                            {messages.length === 0 ? (
                              <p className="text-white/60 text-center py-8">Nessun messaggio</p>
                            ) : (
                              messages.map((msg) => (
                                <div key={msg.id} className={`mb-4 ${msg.sender_role === 'client' ? 'text-right' : ''}`}>
                                  <div className={`inline-block max-w-[80%] px-4 py-3 rounded-xl ${
                                    msg.sender_role === 'client' ? 'bg-[#1a365d]' : 'bg-white/10'
                                  }`}>
                                    <p className="text-xs text-white/60 mb-1">{msg.sender_name}</p>
                                    <p className="text-sm">{msg.content}</p>
                                  </div>
                                </div>
                              ))
                            )}
                          </ScrollArea>
                          <div className="flex gap-2">
                            <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                              onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                              placeholder="Scrivi un messaggio..." className="flex-1 bg-white/5 border-white/10" />
                            <Button onClick={sendMessage} disabled={!newMessage.trim()} className="bg-[#00f0ff] text-black">
                              <Send size={18} />
                            </Button>
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ) : (
                <Card className="glass-card h-full flex items-center justify-center">
                  <CardContent className="text-center py-12">
                    <FolderOpen size={48} className="mx-auto mb-4 text-white/40" />
                    <p className="text-white/60">Seleziona un progetto per vedere i dettagli</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ADMIN DASHBOARD (CONTINUAZIONE NEL PROSSIMO FILE) ====================
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [toolLeads, setToolLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState("bookings");
  const [newProject, setNewProject] = useState({ client_id: "", name: "", description: "", service_type: "web" });
  const [selectedProject, setSelectedProject] = useState(null);

  const token = localStorage.getItem("token");

  const loadData = useCallback(async () => {
    try {
      const [statsRes, contactsRes, consultationsRes, clientsRes, projectsRes, toolLeadsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/consultations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tool-leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setConsultations(consultationsRes.data);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setToolLeads(toolLeadsRes.data);
      setBookings(bookingsRes.data);
    } catch {
      localStorage.removeItem("token");
      navigate("/login-admin");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/login-admin"); return; }
    loadData();
  }, [token, navigate, loadData]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    navigate("/");
  };

  const updateStatus = async (type, id, status) => {
    try {
      await axios.patch(`${API}/${type}/${id}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      loadData();
    } catch { }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm("Eliminare?")) return;
    try {
      await axios.delete(`${API}/${type}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadData();
    } catch { }
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, newProject, { headers: { Authorization: `Bearer ${token}` } });
      setNewProject({ client_id: "", name: "", description: "", service_type: "web" });
      loadData();
    } catch { }
  };

  const updateProject = async (projectId, data) => {
    try {
      await axios.patch(`${API}/projects/${projectId}`, data, { headers: { Authorization: `Bearer ${token}` } });
      loadData();
    } catch { }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#00f0ff]" size={48} /></div>;

  return (
    <div className="relative min-h-screen pt-28 md:pt-32 pb-12" data-testid="admin-dashboard">
      <SEOHead title="Dashboard Admin" description="Pannello amministratore SIMO Marketing" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">Dashboard <span className="text-[#00f0ff]">Admin</span></h1>
          <Button variant="outline" onClick={handleLogout} className="btn-secondary" data-testid="logout-btn">
            <LogOut size={18} className="mr-2" /> Esci
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3 mb-8">
            {[
              { label: "Contatti", value: stats.total_contacts, color: "text-[#00f0ff]" },
              { label: "Nuovi", value: stats.new_contacts, color: "text-yellow-400" },
              { label: "Consulenze", value: stats.total_consultations, color: "text-[#00f0ff]" },
              { label: "In Attesa", value: stats.pending_consultations, color: "text-yellow-400" },
              { label: "Clienti", value: stats.total_clients, color: "text-green-400" },
              { label: "Progetti", value: stats.total_projects, color: "text-purple-400" },
              { label: "Lead Tools", value: stats.total_tool_leads || 0, color: "text-orange-400" },
              { label: "Nuovi Lead", value: stats.new_tool_leads || 0, color: "text-yellow-400" },
              { label: "Prenotazioni", value: stats.total_bookings || 0, color: "text-pink-400" },
              { label: "In Arrivo", value: stats.upcoming_bookings || 0, color: "text-green-400" },
            ].map((stat, i) => (
              <Card key={i} className="glass-card">
                <CardContent className="p-4 text-center">
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                  <div className="text-white/60 text-xs">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white/5 flex-wrap">
            <TabsTrigger value="bookings">Prenotazioni</TabsTrigger>
            <TabsTrigger value="contacts">Contatti</TabsTrigger>
            <TabsTrigger value="consultations">Consulenze</TabsTrigger>
            <TabsTrigger value="tool-leads">Lead Tools</TabsTrigger>
            <TabsTrigger value="clients">Clienti</TabsTrigger>
            <TabsTrigger value="projects">Progetti</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <div className="space-y-4">
              <Card className="glass-card mb-4">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="text-pink-400" size={24} />
                    <div>
                      <h3 className="font-bold">Prenotazioni Chiamate</h3>
                      <p className="text-white/60 text-sm">Clienti che hanno prenotato una chiamata telefonica</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {bookings.length === 0 ? (
                <Card className="glass-card"><CardContent className="p-8 text-center text-white/60">Nessuna prenotazione</CardContent></Card>
              ) : bookings.map((booking) => (
                <Card key={booking.id} className={`glass-card ${booking.status === 'confirmed' ? 'border-l-4 border-l-green-500' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className={
                            booking.status === "confirmed" ? "bg-green-500/20 text-green-400" :
                            booking.status === "completed" ? "bg-blue-500/20 text-blue-400" :
                            booking.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                            "bg-yellow-500/20 text-yellow-400"
                          }>
                            {booking.status === "confirmed" ? "Confermata" : 
                             booking.status === "completed" ? "Completata" :
                             booking.status === "cancelled" ? "Annullata" : "No Show"}
                          </Badge>
                          <span className="text-[#00f0ff] font-semibold">
                            {new Date(booking.date).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })} ore {booking.time}
                          </span>
                        </div>
                        <div className="grid md:grid-cols-2 gap-2">
                          <div className="flex items-center gap-2">
                            <User size={14} className="text-white/40" />
                            <span className="font-medium">{booking.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone size={14} className="text-white/40" />
                            <a href={`tel:${booking.phone}`} className="text-[#00f0ff] hover:underline">{booking.phone}</a>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail size={14} className="text-white/40" />
                            <span className="text-white/60 text-sm">{booking.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Target size={14} className="text-white/40" />
                            <span className="text-white/60 text-sm capitalize">{booking.service_interest.replace('_', ' ')}</span>
                          </div>
                        </div>
                        {booking.notes && (
                          <p className="text-white/50 text-sm mt-2 italic">"{booking.notes}"</p>
                        )}
                        <p className="text-white/40 text-xs mt-2">Prenotato: {new Date(booking.created_at).toLocaleString('it-IT')}</p>
                      </div>
                      <div className="flex gap-2">
                        <Select value={booking.status} onValueChange={(v) => updateStatus("bookings", booking.id, v)}>
                          <SelectTrigger className="w-28 bg-white/5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="confirmed">Confermata</SelectItem>
                            <SelectItem value="completed">Completata</SelectItem>
                            <SelectItem value="cancelled">Annullata</SelectItem>
                            <SelectItem value="no_show">No Show</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="destructive" size="sm" onClick={() => deleteItem("bookings", booking.id)}><X size={16} /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="space-y-4">
              {contacts.length === 0 ? (
                <Card className="glass-card"><CardContent className="p-8 text-center text-white/60">Nessun contatto</CardContent></Card>
              ) : contacts.map((c) => (
                <Card key={c.id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-bold">{c.name}</span>
                          <Badge className={c.status === "new" ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}>{c.status}</Badge>
                        </div>
                        <p className="text-white/60 text-sm">{c.email} | {c.phone}</p>
                        <p className="font-medium mt-1">{c.subject}</p>
                        <p className="text-white/60 text-sm">{c.message}</p>
                      </div>
                      <div className="flex gap-2">
                        <Select value={c.status} onValueChange={(v) => updateStatus("contacts", c.id, v)}>
                          <SelectTrigger className="w-28 bg-white/5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">Nuovo</SelectItem>
                            <SelectItem value="contacted">Contattato</SelectItem>
                            <SelectItem value="completed">Completato</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="destructive" size="sm" onClick={() => deleteItem("contacts", c.id)}><X size={16} /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="consultations">
            <div className="space-y-4">
              {consultations.length === 0 ? (
                <Card className="glass-card"><CardContent className="p-8 text-center text-white/60">Nessuna consulenza</CardContent></Card>
              ) : consultations.map((c) => (
                <Card key={c.id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-bold">{c.name}</span>
                          <Badge className={c.status === "pending" ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}>{c.status}</Badge>
                        </div>
                        <p className="text-white/60 text-sm">{c.email} | {c.phone}</p>
                        <div className="flex gap-2 mt-2"><Badge className="bg-[#00f0ff]/20 text-[#00f0ff]">{c.service_type}</Badge>{c.budget && <Badge>{c.budget}</Badge>}</div>
                        <p className="text-white/60 text-sm mt-2">{c.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Select value={c.status} onValueChange={(v) => updateStatus("consultations", c.id, v)}>
                          <SelectTrigger className="w-28 bg-white/5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">In Attesa</SelectItem>
                            <SelectItem value="scheduled">Programmata</SelectItem>
                            <SelectItem value="completed">Completata</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="destructive" size="sm" onClick={() => deleteItem("consultations", c.id)}><X size={16} /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="tool-leads">
            <div className="space-y-4">
              <Card className="glass-card mb-4">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Wrench className="text-orange-400" size={24} />
                    <div>
                      <h3 className="font-bold">Lead dai Tools Gratuiti</h3>
                      <p className="text-white/60 text-sm">Potenziali clienti che hanno usato i tool SEO, Rank Checker, Social Analyzer, ecc.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {toolLeads.length === 0 ? (
                <Card className="glass-card"><CardContent className="p-8 text-center text-white/60">Nessun lead dai tools</CardContent></Card>
              ) : toolLeads.map((lead) => (
                <Card key={lead.id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className="bg-orange-500/20 text-orange-400 capitalize">{lead.tool_type.replace('-', ' ')}</Badge>
                          <Badge className={lead.status === "new" ? "bg-yellow-500/20 text-yellow-400" : lead.status === "contacted" ? "bg-blue-500/20 text-blue-400" : "bg-green-500/20 text-green-400"}>{lead.status}</Badge>
                          {lead.result_score !== null && (
                            <span className={`text-sm font-bold ${lead.result_score >= 70 ? 'text-green-400' : lead.result_score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                              Score: {lead.result_score}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                          <Globe size={14} className="text-[#00f0ff]" />
                          <span className="font-medium">{lead.domain}</span>
                          <a href={lead.url.startsWith('http') ? lead.url : `https://${lead.url}`} target="_blank" rel="noopener noreferrer" className="text-[#00f0ff] hover:underline">
                            <ExternalLink size={14} />
                          </a>
                        </div>
                        {lead.keyword && (
                          <div className="flex items-center gap-2 mb-1">
                            <Search size={14} className="text-white/40" />
                            <span className="text-white/60 text-sm">Keyword: <span className="text-white">{lead.keyword}</span></span>
                          </div>
                        )}
                        <p className="text-white/40 text-xs mt-2">{new Date(lead.created_at).toLocaleString('it-IT')}</p>
                      </div>
                      <div className="flex gap-2">
                        <Select value={lead.status} onValueChange={(v) => updateStatus("tool-leads", lead.id, v)}>
                          <SelectTrigger className="w-28 bg-white/5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">Nuovo</SelectItem>
                            <SelectItem value="contacted">Contattato</SelectItem>
                            <SelectItem value="converted">Convertito</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="destructive" size="sm" onClick={() => deleteItem("tool-leads", lead.id)}><X size={16} /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="clients">
            <div className="space-y-4">
              {clients.length === 0 ? (
                <Card className="glass-card"><CardContent className="p-8 text-center text-white/60">Nessun cliente registrato</CardContent></Card>
              ) : clients.map((c) => (
                <Card key={c.id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#00f0ff]/10 flex items-center justify-center">
                        <User className="text-[#00f0ff]" />
                      </div>
                      <div>
                        <p className="font-bold">{c.name}</p>
                        <p className="text-white/60 text-sm">{c.email}</p>
                        {c.company && <p className="text-white/40 text-xs">{c.company}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="projects">
            <Card className="glass-card mb-6">
              <CardHeader><CardTitle>Nuovo Progetto</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={createProject} className="grid md:grid-cols-4 gap-4">
                  <Select value={newProject.client_id} onValueChange={(v) => setNewProject({ ...newProject, client_id: v })}>
                    <SelectTrigger className="bg-white/5"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Nome progetto" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} className="bg-white/5" />
                  <Input placeholder="Descrizione" value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} className="bg-white/5" />
                  <Button type="submit" className="btn-primary" disabled={!newProject.client_id || !newProject.name}>Crea Progetto</Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {projects.length === 0 ? (
                <Card className="glass-card"><CardContent className="p-8 text-center text-white/60">Nessun progetto</CardContent></Card>
              ) : projects.map((p) => (
                <Card key={p.id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-bold">{p.name}</span>
                          <Badge className={
                            p.status === "completato" ? "bg-green-500/20 text-green-400" :
                            p.status === "in_lavorazione" ? "bg-blue-500/20 text-blue-400" :
                            "bg-yellow-500/20 text-yellow-400"
                          }>{p.status}</Badge>
                        </div>
                        <p className="text-white/60 text-sm">{p.description}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex-1">
                            <Progress value={p.progress} className="h-2" />
                          </div>
                          <span className="text-sm text-[#00f0ff]">{p.progress}%</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Select value={p.status} onValueChange={(v) => updateProject(p.id, { status: v })}>
                          <SelectTrigger className="w-32 bg-white/5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="in_attesa">In Attesa</SelectItem>
                            <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                            <SelectItem value="revisione">Revisione</SelectItem>
                            <SelectItem value="completato">Completato</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={String(p.progress)} onValueChange={(v) => updateProject(p.id, { progress: parseInt(v) })}>
                          <SelectTrigger className="w-20 bg-white/5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((v) => (
                              <SelectItem key={v} value={String(v)}>{v}%</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="destructive" size="sm" onClick={() => deleteItem("projects", p.id)}><X size={16} /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ==================== TOOLS HUB PAGE ====================
const ToolsPage = () => {
  const tools = [
    {
      icon: Search,
      title: "Analisi SEO",
      desc: "Analizza la SEO del tuo sito web e ottieni raccomandazioni",
      path: "/tools/seo",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: TrendingUp,
      title: "Google Rank Checker",
      desc: "Scopri la posizione del tuo sito su Google per una keyword",
      path: "/tools/rank",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Share2,
      title: "Analisi Social",
      desc: "Analizza i tuoi profili social e migliora la presenza online",
      path: "/tools/social",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: Zap,
      title: "Speed Test",
      desc: "Misura la velocità di caricamento del tuo sito web",
      path: "/tools/speed",
      color: "from-yellow-500 to-orange-500"
    },
    {
      icon: Shield,
      title: "SSL Checker",
      desc: "Verifica lo stato del certificato SSL del tuo sito",
      path: "/tools/ssl",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: Smartphone,
      title: "Mobile Friendly Test",
      desc: "Verifica se il tuo sito è ottimizzato per dispositivi mobili",
      path: "/tools/mobile",
      color: "from-purple-500 to-violet-500"
    },
    {
      icon: Lock,
      title: "Security Headers",
      desc: "Analizza gli header di sicurezza HTTP del tuo sito",
      path: "/tools/security",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: Eye,
      title: "Cyber Security Scanner",
      desc: "Analizza IP, geolocalizzazione e traccia visitatori del tuo sito",
      path: "/tools/cyber",
      color: "from-red-600 to-orange-600",
      isNew: true
    },
  ];

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="tools-page">
      <SEOHead title="Tools Gratuiti" description="Strumenti SEO gratuiti: analisi sito, rank checker Google, analisi social, speed test, SSL checker e molto altro. Migliora il tuo sito web gratis!" />

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30">100% GRATUITI</Badge>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Tools <span className="text-[#00f0ff]">Gratuiti</span>
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
              Analizza e migliora il tuo sito web con i nostri strumenti professionali gratuiti
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {tools.map((tool, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={tool.path}>
                  <Card className="glass-card h-full group hover:border-[#00f0ff]/50 transition-all cursor-pointer overflow-hidden relative">
                    {tool.isNew && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                        NEW
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <tool.icon className="text-white" size={28} />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold mb-2 group-hover:text-[#00f0ff] transition-colors">
                        {tool.title}
                      </h3>
                      <p className="text-white/60 text-sm">{tool.desc}</p>
                      <div className="mt-4 flex items-center text-[#00f0ff] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Usa Tool <ArrowRight size={16} className="ml-2" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <Card className="glass-card p-8 sm:p-12">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">
                Hai bisogno di un'analisi <span className="text-[#00f0ff]">approfondita</span>?
              </h2>
              <p className="text-white/60 mb-6 max-w-xl mx-auto">
                I nostri esperti possono analizzare il tuo sito e creare una strategia personalizzata per migliorare traffico e conversioni
              </p>
              <Link to="/consulenza">
                <Button size="lg" className="btn-primary">
                  Consulenza Gratuita <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// ==================== SPEED TEST PAGE ====================
const SpeedTestPage = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runTest = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/tools/speed-test?url=${encodeURIComponent(url.trim())}`);
      setResult(response.data);
      // Save lead
      if (response.data.success) {
        try {
          await axios.post(`${API}/tool-leads`, {
            tool_type: "speed",
            url: url.trim(),
            result_score: response.data.score
          });
        } catch { }
      }
    } catch {
      setResult({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => score >= 80 ? "#00ff9d" : score >= 60 ? "#ffbd00" : "#ff0055";

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="speed-test-page">
      <SEOHead title="Speed Test Gratuito" description="Testa gratuitamente la velocità del tuo sito web. Analisi TTFB, dimensioni pagina e raccomandazioni." />

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <Link to="/tools" className="inline-flex items-center text-[#00f0ff] hover:underline mb-6">
            <ArrowRight className="rotate-180 mr-2" size={16} /> Tutti i Tools
          </Link>
          <Badge className="mb-4 bg-yellow-500/10 text-yellow-400 border-yellow-500/30">SPEED TEST</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold mb-6">Test <span className="text-[#00f0ff]">Velocità</span> Sito</h1>
          
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyPress={(e) => e.key === "Enter" && runTest()}
              placeholder="https://esempio.com" className="flex-1 h-12 sm:h-14 bg-white/5" />
            <Button onClick={runTest} disabled={loading || !url.trim()} className="h-12 sm:h-14 px-8 btn-primary">
              {loading ? <Loader2 className="animate-spin" /> : <><Zap size={20} className="mr-2" />Testa</>}
            </Button>
          </div>
        </div>
      </section>

      {result && result.success && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <Card className="glass-card mb-6">
              <CardContent className="p-6 sm:p-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="text-2xl sm:text-3xl font-bold" style={{ color: getScoreColor(result.score) }}>{result.score}</div>
                    <div className="text-xs text-white/60">Punteggio</div>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="text-2xl sm:text-3xl font-bold text-[#00f0ff]">{result.ttfb_ms}ms</div>
                    <div className="text-xs text-white/60">TTFB</div>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="text-2xl sm:text-3xl font-bold text-white">{result.html_size_kb}KB</div>
                    <div className="text-xs text-white/60">Dimensione</div>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="text-2xl sm:text-3xl font-bold text-white">{result.images}</div>
                    <div className="text-xs text-white/60">Immagini</div>
                  </div>
                </div>
                <ul className="space-y-2">
                  {result.recommendations?.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      {rec.startsWith("✅") || rec.startsWith("🚀") ? <CheckCircle size={16} className="text-green-400 mt-0.5" /> :
                       rec.startsWith("❌") || rec.startsWith("🐌") ? <XCircle size={16} className="text-red-400 mt-0.5" /> :
                       <AlertCircle size={16} className="text-yellow-400 mt-0.5" />}
                      <span className="text-white/80">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>
      )}
    </div>
  );
};

// ==================== SSL CHECKER PAGE ====================
const SSLCheckerPage = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const checkSSL = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/tools/ssl-check?url=${encodeURIComponent(url.trim())}`);
      setResult(response.data);
      // Save lead
      if (response.data.success) {
        try {
          await axios.post(`${API}/tool-leads`, {
            tool_type: "ssl",
            url: url.trim(),
            result_score: response.data.score
          });
        } catch { }
      }
    } catch {
      setResult({ error: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="ssl-checker-page">
      <SEOHead title="SSL Checker Gratuito" description="Verifica gratuitamente il certificato SSL del tuo sito. Controlla scadenza e sicurezza." />

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <Link to="/tools" className="inline-flex items-center text-[#00f0ff] hover:underline mb-6">
            <ArrowRight className="rotate-180 mr-2" size={16} /> Tutti i Tools
          </Link>
          <Badge className="mb-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/30">SSL CHECKER</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold mb-6">Verifica <span className="text-[#00f0ff]">Certificato SSL</span></h1>
          
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyPress={(e) => e.key === "Enter" && checkSSL()}
              placeholder="https://esempio.com" className="flex-1 h-12 sm:h-14 bg-white/5" />
            <Button onClick={checkSSL} disabled={loading || !url.trim()} className="h-12 sm:h-14 px-8 btn-primary">
              {loading ? <Loader2 className="animate-spin" /> : <><Shield size={20} className="mr-2" />Verifica</>}
            </Button>
          </div>
        </div>
      </section>

      {result && result.success && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <Card className="glass-card">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${result.days_until_expiry > 30 ? 'bg-green-500/20' : result.days_until_expiry > 0 ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                    <Shield size={32} className={result.days_until_expiry > 30 ? 'text-green-400' : result.days_until_expiry > 0 ? 'text-yellow-400' : 'text-red-400'} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{result.hostname}</h3>
                    <p className="text-white/60">Emesso da: {result.issuer}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="text-2xl font-bold text-[#00f0ff]">{result.days_until_expiry}</div>
                    <div className="text-xs text-white/60">giorni rimanenti</div>
                  </div>
                </div>
                <ul className="space-y-2">
                  {result.recommendations?.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      {rec.startsWith("✅") ? <CheckCircle size={16} className="text-green-400 mt-0.5" /> :
                       rec.startsWith("❌") || rec.startsWith("🚨") ? <XCircle size={16} className="text-red-400 mt-0.5" /> :
                       <AlertCircle size={16} className="text-yellow-400 mt-0.5" />}
                      <span className="text-white/80">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>
      )}
    </div>
  );
};

// ==================== MOBILE TEST PAGE ====================
const MobileTestPage = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runTest = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/tools/mobile-test?url=${encodeURIComponent(url.trim())}`);
      setResult(response.data);
      // Save lead
      if (response.data.success) {
        try {
          await axios.post(`${API}/tool-leads`, {
            tool_type: "mobile",
            url: url.trim(),
            result_score: response.data.score
          });
        } catch { }
      }
    } catch {
      setResult({ error: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="mobile-test-page">
      <SEOHead title="Mobile Friendly Test Gratuito" description="Verifica se il tuo sito è ottimizzato per dispositivi mobili. Test gratuito e raccomandazioni." />

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <Link to="/tools" className="inline-flex items-center text-[#00f0ff] hover:underline mb-6">
            <ArrowRight className="rotate-180 mr-2" size={16} /> Tutti i Tools
          </Link>
          <Badge className="mb-4 bg-purple-500/10 text-purple-400 border-purple-500/30">MOBILE TEST</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold mb-6">Test <span className="text-[#00f0ff]">Mobile Friendly</span></h1>
          
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyPress={(e) => e.key === "Enter" && runTest()}
              placeholder="https://esempio.com" className="flex-1 h-12 sm:h-14 bg-white/5" />
            <Button onClick={runTest} disabled={loading || !url.trim()} className="h-12 sm:h-14 px-8 btn-primary">
              {loading ? <Loader2 className="animate-spin" /> : <><Smartphone size={20} className="mr-2" />Testa</>}
            </Button>
          </div>
        </div>
      </section>

      {result && result.success && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <Card className="glass-card">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center ${result.score >= 80 ? 'bg-green-500/20' : result.score >= 60 ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                    <Smartphone size={40} className={result.score >= 80 ? 'text-green-400' : result.score >= 60 ? 'text-yellow-400' : 'text-red-400'} />
                  </div>
                  <div className="text-left">
                    <div className="text-3xl font-bold" style={{ color: result.score >= 80 ? '#00ff9d' : result.score >= 60 ? '#ffbd00' : '#ff0055' }}>{result.score}/100</div>
                    <div className="text-white/60">Punteggio Mobile</div>
                  </div>
                </div>
                <ul className="space-y-2">
                  {result.recommendations?.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      {rec.startsWith("✅") || rec.startsWith("📱") ? <CheckCircle size={16} className="text-green-400 mt-0.5" /> :
                       rec.startsWith("❌") ? <XCircle size={16} className="text-red-400 mt-0.5" /> :
                       <AlertCircle size={16} className="text-yellow-400 mt-0.5" />}
                      <span className="text-white/80">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>
      )}
    </div>
  );
};

// ==================== SECURITY HEADERS PAGE ====================
const SecurityHeadersPage = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/tools/headers-check?url=${encodeURIComponent(url.trim())}`);
      setResult(response.data);
      // Save lead
      if (response.data.success) {
        try {
          await axios.post(`${API}/tool-leads`, {
            tool_type: "security",
            url: url.trim(),
            result_score: response.data.score
          });
        } catch { }
      }
    } catch {
      setResult({ error: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="security-headers-page">
      <SEOHead title="Security Headers Check Gratuito" description="Analizza gli header di sicurezza HTTP del tuo sito. Test gratuito HSTS, CSP, X-Frame-Options e altro." />

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <Link to="/tools" className="inline-flex items-center text-[#00f0ff] hover:underline mb-6">
            <ArrowRight className="rotate-180 mr-2" size={16} /> Tutti i Tools
          </Link>
          <Badge className="mb-4 bg-red-500/10 text-red-400 border-red-500/30">SECURITY CHECK</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold mb-6">Analisi <span className="text-[#00f0ff]">Security Headers</span></h1>
          
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyPress={(e) => e.key === "Enter" && runCheck()}
              placeholder="https://esempio.com" className="flex-1 h-12 sm:h-14 bg-white/5" />
            <Button onClick={runCheck} disabled={loading || !url.trim()} className="h-12 sm:h-14 px-8 btn-primary">
              {loading ? <Loader2 className="animate-spin" /> : <><Lock size={20} className="mr-2" />Analizza</>}
            </Button>
          </div>
        </div>
      </section>

      {result && result.success && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <Card className="glass-card">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold" style={{ color: result.score >= 80 ? '#00ff9d' : result.score >= 50 ? '#ffbd00' : '#ff0055' }}>{result.score}/100</div>
                    <div className="text-white/60">Security Score</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  {result.checks && Object.entries(result.checks).map(([header, present]) => (
                    <div key={header} className={`p-3 rounded-lg text-center ${present ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                      {present ? <CheckCircle size={20} className="mx-auto text-green-400 mb-1" /> : <XCircle size={20} className="mx-auto text-red-400 mb-1" />}
                      <div className="text-xs text-white/60 uppercase">{header.replace(/-/g, ' ')}</div>
                    </div>
                  ))}
                </div>
                <ul className="space-y-2">
                  {result.recommendations?.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      {rec.startsWith("✅") || rec.startsWith("🛡️") ? <CheckCircle size={16} className="text-green-400 mt-0.5" /> :
                       rec.startsWith("❌") || rec.startsWith("🚨") ? <XCircle size={16} className="text-red-400 mt-0.5" /> :
                       <AlertCircle size={16} className="text-yellow-400 mt-0.5" />}
                      <span className="text-white/80">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>
      )}
    </div>
  );
};

// ==================== CYBER SECURITY SCANNER PAGE ====================
const CyberSecurityPage = () => {
  const [loading, setLoading] = useState(true);
  const [visitorInfo, setVisitorInfo] = useState(null);
  const [ipLookup, setIpLookup] = useState("");
  const [ipResult, setIpResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    loadVisitorInfo();
  }, []);

  const loadVisitorInfo = async () => {
    try {
      const response = await axios.get(`${API}/tools/visitor-info`);
      setVisitorInfo(response.data);
      // Save lead
      try {
        await axios.post(`${API}/tool-leads`, {
          tool_type: "cyber",
          url: response.data.ip || "visitor",
          result_score: response.data.security?.score || 50
        });
      } catch { }
    } catch {
      // Error loading visitor info - silently fail
    } finally {
      setLoading(false);
    }
  };

  const lookupIP = async () => {
    if (!ipLookup.trim()) return;
    setLookupLoading(true);
    setIpResult(null);
    try {
      const response = await axios.get(`${API}/tools/ip-lookup?ip=${encodeURIComponent(ipLookup.trim())}`);
      setIpResult(response.data);
    } catch {
      setIpResult({ error: true });
    } finally {
      setLookupLoading(false);
    }
  };

  const getThreatColor = (level) => {
    switch (level) {
      case "high": return "text-red-500 bg-red-500/20";
      case "medium": return "text-yellow-500 bg-yellow-500/20";
      default: return "text-green-500 bg-green-500/20";
    }
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="cyber-security-page">
      <SEOHead title="Cyber Security Scanner" description="Analizza IP, traccia visitatori, verifica geolocalizzazione e sicurezza. Strumento gratuito per la cyber security." />

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <Badge className="mb-4 bg-red-500/10 text-red-400 border-red-500/30">CYBER SECURITY</Badge>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              <span className="text-red-400">Cyber Security</span> Scanner
            </h1>
            <p className="text-white/70 max-w-2xl mx-auto">
              Analizza IP, geolocalizzazione, dispositivi e potenziali minacce in tempo reale
            </p>
          </motion.div>

          {/* Your Connection Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="glass-card mb-8 border-l-4 border-l-red-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Eye className="text-red-400" size={20} />
                  </div>
                  La Tua Connessione
                </CardTitle>
                <p className="text-white/60 text-sm">Queste sono le informazioni che i siti web possono vedere su di te</p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="animate-spin text-red-400" size={32} />
                  </div>
                ) : visitorInfo ? (
                  <div className="space-y-6">
                    {/* Security Score */}
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      <div>
                        <h3 className="font-bold">Security Score</h3>
                        <p className="text-white/60 text-sm">Livello di sicurezza della tua connessione</p>
                      </div>
                      <div className="text-right">
                        <div className={`text-3xl font-bold ${visitorInfo.security?.score >= 80 ? 'text-green-400' : visitorInfo.security?.score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {visitorInfo.security?.score || 0}/100
                        </div>
                        <Badge className={getThreatColor(visitorInfo.security?.threat_level)}>
                          {visitorInfo.security?.threat_level === "high" ? "Rischio Alto" :
                           visitorInfo.security?.threat_level === "medium" ? "Rischio Medio" : "Sicuro"}
                        </Badge>
                      </div>
                    </div>

                    {/* Security Issues */}
                    {visitorInfo.security?.issues?.length > 0 && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <h4 className="font-bold text-red-400 mb-2 flex items-center gap-2">
                          <AlertCircle size={18} />
                          Problemi Rilevati
                        </h4>
                        <ul className="space-y-1">
                          {visitorInfo.security.issues.map((issue, i) => (
                            <li key={i} className="text-sm text-white/70">• {issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Info Grid */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Connection Info */}
                      <div className="space-y-3">
                        <h4 className="font-bold text-[#00f0ff] flex items-center gap-2">
                          <Globe size={16} />
                          Connessione
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                            <span className="text-white/60 text-sm">Indirizzo IP</span>
                            <span className="font-mono text-sm">{visitorInfo.ip}</span>
                          </div>
                          {visitorInfo.location && (
                            <>
                              <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                                <span className="text-white/60 text-sm">Paese</span>
                                <span className="text-sm">{visitorInfo.location.country} {visitorInfo.location.country_code && `(${visitorInfo.location.country_code})`}</span>
                              </div>
                              <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                                <span className="text-white/60 text-sm">Città</span>
                                <span className="text-sm">{visitorInfo.location.city}, {visitorInfo.location.region}</span>
                              </div>
                              <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                                <span className="text-white/60 text-sm">ISP</span>
                                <span className="text-sm truncate max-w-[180px]">{visitorInfo.location.isp}</span>
                              </div>
                              <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                                <span className="text-white/60 text-sm">Timezone</span>
                                <span className="text-sm">{visitorInfo.location.timezone}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Device Info */}
                      <div className="space-y-3">
                        <h4 className="font-bold text-[#00f0ff] flex items-center gap-2">
                          <Smartphone size={16} />
                          Dispositivo
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                            <span className="text-white/60 text-sm">Browser</span>
                            <span className="text-sm">{visitorInfo.browser}</span>
                          </div>
                          <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                            <span className="text-white/60 text-sm">Sistema Operativo</span>
                            <span className="text-sm">{visitorInfo.os}</span>
                          </div>
                          <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                            <span className="text-white/60 text-sm">Tipo Dispositivo</span>
                            <span className="text-sm">{visitorInfo.device_type}</span>
                          </div>
                          <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                            <span className="text-white/60 text-sm">Lingua</span>
                            <span className="text-sm">{visitorInfo.headers?.accept_language?.split(',')[0] || 'N/A'}</span>
                          </div>
                          {visitorInfo.location && (
                            <>
                              <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                                <span className="text-white/60 text-sm">VPN/Proxy</span>
                                <Badge className={visitorInfo.location.is_proxy ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}>
                                  {visitorInfo.location.is_proxy ? "Rilevato" : "No"}
                                </Badge>
                              </div>
                              <div className="flex justify-between items-center p-2 bg-white/5 rounded">
                                <span className="text-white/60 text-sm">Rete Mobile</span>
                                <Badge className={visitorInfo.location.is_mobile_network ? "bg-blue-500/20 text-blue-400" : "bg-white/10 text-white/60"}>
                                  {visitorInfo.location.is_mobile_network ? "Sì" : "No"}
                                </Badge>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Map Preview if coordinates available */}
                    {visitorInfo.location?.latitude && visitorInfo.location?.longitude && (
                      <div className="p-4 bg-white/5 rounded-lg">
                        <h4 className="font-bold mb-2 flex items-center gap-2">
                          <MapPin size={16} className="text-red-400" />
                          Posizione Approssimativa
                        </h4>
                        <p className="text-white/60 text-sm mb-2">
                          Coordinate: {visitorInfo.location.latitude.toFixed(4)}, {visitorInfo.location.longitude.toFixed(4)}
                        </p>
                        <a
                          href={`https://www.google.com/maps?q=${visitorInfo.location.latitude},${visitorInfo.location.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#00f0ff] hover:underline text-sm"
                        >
                          <ExternalLink size={14} />
                          Vedi su Google Maps
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-white/60 text-center py-4">Impossibile caricare le informazioni</p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* IP Lookup Tool */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <Search className="text-orange-400" size={20} />
                  </div>
                  Ricerca IP
                </CardTitle>
                <p className="text-white/60 text-sm">Analizza qualsiasi indirizzo IP per ottenere informazioni dettagliate</p>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-6">
                  <Input
                    placeholder="Inserisci un indirizzo IP (es. 8.8.8.8)"
                    value={ipLookup}
                    onChange={(e) => setIpLookup(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && lookupIP()}
                    className="bg-white/5 flex-1"
                  />
                  <Button onClick={lookupIP} disabled={lookupLoading || !ipLookup.trim()} className="btn-primary">
                    {lookupLoading ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
                  </Button>
                </div>

                {ipResult && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    {ipResult.success ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                          <div>
                            <span className="text-white/60 text-sm">IP Analizzato</span>
                            <div className="font-mono text-lg">{ipResult.ip}</div>
                          </div>
                          <Badge className={getThreatColor(ipResult.security?.threat_level)}>
                            {ipResult.security?.threat_level === "high" ? "Rischio Alto" :
                             ipResult.security?.threat_level === "medium" ? "Rischio Medio" : "Sicuro"}
                          </Badge>
                        </div>

                        {ipResult.security?.threat_reasons?.length > 0 && (
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                            <h4 className="font-bold text-yellow-400 text-sm mb-1">Avvisi</h4>
                            {ipResult.security.threat_reasons.map((r, i) => (
                              <p key={i} className="text-sm text-white/70">• {r}</p>
                            ))}
                          </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-bold text-sm mb-2">Posizione</h4>
                            <div className="space-y-1 text-sm">
                              <p><span className="text-white/60">Paese:</span> {ipResult.location?.country}</p>
                              <p><span className="text-white/60">Città:</span> {ipResult.location?.city}, {ipResult.location?.region}</p>
                              <p><span className="text-white/60">CAP:</span> {ipResult.location?.zip || "N/A"}</p>
                              <p><span className="text-white/60">Timezone:</span> {ipResult.location?.timezone}</p>
                            </div>
                          </div>
                          <div>
                            <h4 className="font-bold text-sm mb-2">Rete</h4>
                            <div className="space-y-1 text-sm">
                              <p><span className="text-white/60">ISP:</span> {ipResult.network?.isp}</p>
                              <p><span className="text-white/60">Org:</span> {ipResult.network?.organization}</p>
                              <p><span className="text-white/60">AS:</span> {ipResult.network?.as_number}</p>
                            </div>
                          </div>
                        </div>

                        {ipResult.location?.latitude && (
                          <a
                            href={`https://www.google.com/maps?q=${ipResult.location.latitude},${ipResult.location.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-[#00f0ff] hover:underline text-sm"
                          >
                            <MapPin size={14} />
                            Vedi posizione su mappa
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <XCircle size={32} className="text-red-400 mx-auto mb-2" />
                        <p className="text-white/60">{ipResult.error || "IP non valido o riservato"}</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* CTA for Cyber Security Service */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-8">
            <Card className="glass-card bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/30">
              <CardContent className="p-8 text-center">
                <Shield size={48} className="text-red-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Proteggi la Tua Azienda</h3>
                <p className="text-white/60 mb-6 max-w-xl mx-auto">
                  Offriamo servizi completi di Cyber Security per aziende e privati: audit, penetration testing, monitoraggio 24/7 e formazione del personale.
                </p>
                <Link to="/consulenza">
                  <Button className="btn-primary px-8">
                    Richiedi Consulenza <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// ==================== RANK CHECKER PAGE ====================
const RankCheckerPage = () => {
  const [url, setUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const checkRank = async () => {
    if (!url.trim() || !keyword.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/rank/check?url=${encodeURIComponent(url.trim())}&keyword=${encodeURIComponent(keyword.trim())}`);
      setResult(response.data);
      // Save lead
      if (response.data.success) {
        try {
          await axios.post(`${API}/tool-leads`, {
            tool_type: "rank",
            url: url.trim(),
            keyword: keyword.trim(),
            result_score: response.data.position ? Math.max(0, 100 - response.data.position * 2) : 0
          });
        } catch { }
      }
    } catch {
      setResult({ error: true, recommendations: ["Errore durante l'analisi"] });
    } finally {
      setLoading(false);
    }
  };

  const getPositionColor = (pos) => {
    if (!pos) return "#ff0055";
    if (pos === 1) return "#FFD700";
    if (pos <= 3) return "#00ff9d";
    if (pos <= 10) return "#00f0ff";
    if (pos <= 20) return "#ffbd00";
    return "#ff0055";
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="rank-checker-page">
      <SEOHead title="Google Rank Checker Gratuito" description="Verifica gratuitamente la posizione del tuo sito su Google. Scopri dove appare il tuo sito per le keyword importanti." />

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
            <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30">STRUMENTO GRATUITO</Badge>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
              Google <span className="text-[#00f0ff]">Rank Checker</span>
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto mb-8 sm:mb-12">
              Scopri in che posizione appare il tuo sito su Google per una keyword specifica
            </p>

            <div className="max-w-2xl mx-auto space-y-4">
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                <Input 
                  value={url} 
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Il tuo sito (es: simomarketing.it)" 
                  className="pl-12 h-12 sm:h-14 text-base sm:text-lg bg-white/5 border-white/10" 
                  data-testid="rank-url-input" 
                />
              </div>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                <Input 
                  value={keyword} 
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && checkRank()}
                  placeholder="Keyword da cercare (es: agenzia marketing sicilia)" 
                  className="pl-12 h-12 sm:h-14 text-base sm:text-lg bg-white/5 border-white/10" 
                  data-testid="rank-keyword-input" 
                />
              </div>
              <Button 
                onClick={checkRank} 
                disabled={loading || !url.trim() || !keyword.trim()} 
                className="w-full h-12 sm:h-14 text-base sm:text-lg btn-primary" 
                data-testid="rank-check-btn"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={20} /> : <><Search size={20} className="mr-2" /></>}
                Verifica Posizione
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {result && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {/* Position Result */}
              <Card className="glass-card mb-6">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                    <div className="relative">
                      <div 
                        className="w-28 h-28 sm:w-36 sm:h-36 rounded-full flex items-center justify-center border-4"
                        style={{ borderColor: getPositionColor(result.position) }}
                      >
                        {result.position ? (
                          <div className="text-center">
                            <span className="text-3xl sm:text-5xl font-bold" style={{ color: getPositionColor(result.position) }}>
                              #{result.position}
                            </span>
                          </div>
                        ) : (
                          <XCircle size={48} className="text-red-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h2 className="text-xl sm:text-2xl font-bold mb-2">
                        {result.position ? `Posizione #${result.position} su Google` : "Non trovato nei primi 100 risultati"}
                      </h2>
                      <p className="text-white/60 text-sm sm:text-base mb-2">
                        <span className="text-white/80">{result.domain}</span> per "<span className="text-[#00f0ff]">{result.keyword}</span>"
                      </p>
                      {result.found_url && (
                        <p className="text-white/40 text-xs sm:text-sm truncate">{result.found_url}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start">
                        {result.position && result.position <= 10 && (
                          <Badge className="bg-green-500/20 text-green-400">Prima Pagina</Badge>
                        )}
                        {result.position === 1 && (
                          <Badge className="bg-yellow-500/20 text-yellow-400">🏆 #1</Badge>
                        )}
                        {!result.position && (
                          <Badge className="bg-red-500/20 text-red-400">Non in Top 100</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Top 10 Results */}
              {result.top_results && result.top_results.length > 0 && (
                <Card className="glass-card mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg sm:text-xl">Top 10 Risultati per "{result.keyword}"</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {result.top_results.map((r, i) => (
                        <div 
                          key={i} 
                          className={`flex items-center gap-3 p-3 rounded-lg ${
                            r.domain === result.domain || result.domain?.includes(r.domain) || r.domain?.includes(result.domain)
                              ? 'bg-[#00f0ff]/10 border border-[#00f0ff]/30' 
                              : 'bg-white/5'
                          }`}
                        >
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            r.position === 1 ? 'bg-yellow-500/20 text-yellow-400' :
                            r.position <= 3 ? 'bg-green-500/20 text-green-400' :
                            'bg-white/10 text-white/60'
                          }`}>
                            {r.position}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{r.domain}</p>
                            <p className="text-xs text-white/40 truncate">{r.url}</p>
                          </div>
                          {(r.domain === result.domain || result.domain?.includes(r.domain) || r.domain?.includes(result.domain)) && (
                            <Badge className="bg-[#00f0ff]/20 text-[#00f0ff] text-xs">TU</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recommendations */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl">Raccomandazioni</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {result.recommendations?.map((rec, i) => (
                      <li key={i} className="flex items-start gap-3">
                        {rec.startsWith("🏆") || rec.startsWith("✅") || rec.startsWith("🥈") ? (
                          <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
                        ) : rec.startsWith("❌") || rec.startsWith("⚠️") ? (
                          <AlertCircle size={18} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Zap size={18} className="text-[#00f0ff] flex-shrink-0 mt-0.5" />
                        )}
                        <span className="text-white/80 text-sm sm:text-base">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* CTA */}
              <div className="mt-8 text-center">
                <p className="text-white/60 mb-4">Vuoi migliorare il tuo posizionamento su Google?</p>
                <Link to="/consulenza">
                  <Button className="btn-primary">
                    Richiedi Consulenza SEO <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

// ==================== ALTRE PAGINE (SEMPLIFICATE) ====================
const MOHAMED_PHOTO = "https://customer-assets.emergentagent.com/job_agency-nexus-14/artifacts/gwrhiiwh_WhatsApp%20Image%202026-03-16%20at%2012.12.17%20%281%29.jpeg";

const ChiSiamoPage = () => (
  <div className="relative min-h-screen pt-28 md:pt-32" data-testid="chi-siamo-page">
    <SEOHead title="Chi Siamo" description="SIMO Marketing nasce a Caltanissetta nel 2022. Fondata dall'Ing. Mohamed Marfoq, operiamo in tutta Italia e all'estero." />
    <section className="py-16">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30">LA NOSTRA STORIA</Badge>
          <h1 className="text-3xl lg:text-4xl font-bold mb-6">Chi è <span className="text-[#00f0ff]">SIMO Marketing</span></h1>
        </motion.div>
        <div className="glass-card rounded-2xl p-8 mb-12">
          <p className="text-white/70 mb-4 text-lg">SIMO Marketing nasce a Caltanissetta nel 2022 dalla visione di Mohamed Marfoq, ingegnere informatico con la passione per il digitale.</p>
          <p className="text-white/70 mb-4">Da un piccolo studio di sviluppo web, siamo cresciuti fino a diventare un'agenzia di marketing digitale a 360°, servendo clienti in tutta Italia e all'estero.</p>
          <p className="text-white/70 mb-8">La nostra missione: trasformare le idee dei clienti in esperienze digitali straordinarie.</p>
          <div className="flex gap-8 justify-center">
            <div className="text-center"><div className="text-3xl font-bold text-[#00f0ff]">2022</div><div className="text-sm text-white/60">Fondazione</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-[#00f0ff]">50+</div><div className="text-sm text-white/60">Progetti</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-[#00f0ff]">30+</div><div className="text-sm text-white/60">Clienti</div></div>
          </div>
        </div>
      </div>
    </section>
    <section className="py-16 bg-[#0f172a]/50">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-2xl font-bold mb-8">Il <span className="text-[#00f0ff]">Fondatore</span></h2>
        <Card className="glass-card"><CardContent className="p-8">
          <div className="w-48 h-48 rounded-full mx-auto mb-6 overflow-hidden border-4 border-[#00f0ff]/30">
            <img src={MOHAMED_PHOTO} alt="Mohamed Marfoq" className="w-full h-full object-cover" />
          </div>
          <h3 className="text-xl font-bold">Mohamed Marfoq</h3>
          <p className="text-[#00f0ff] mb-4">Founder & CEO</p>
          <p className="text-white/70 mb-6">Ingegnere Informatico con esperienza nello sviluppo software e marketing digitale. Fondatore di SIMO Marketing nel 2022, con la missione di trasformare le idee in realtà digitali.</p>
          <div className="flex justify-center gap-4">
            <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#00f0ff] transition-colors"><Linkedin size={24} /></a>
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#00f0ff] transition-colors"><Instagram size={24} /></a>
            <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#00f0ff] transition-colors"><Facebook size={24} /></a>
          </div>
        </CardContent></Card>
      </div>
    </section>
  </div>
);

const PortfolioPage = () => {
  const projects = [
    { title: "E-commerce Fashion", category: "Web", image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600", tech: ["React", "Node.js"] },
    { title: "App Fitness", category: "Mobile", image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600", tech: ["React Native"] },
    { title: "Corporate Site", category: "Web", image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600", tech: ["Next.js"] },
    { title: "Food Delivery", category: "Mobile", image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600", tech: ["Flutter"] },
    { title: "SaaS Landing", category: "Landing", image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600", tech: ["React"] },
    { title: "Real Estate", category: "Web", image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600", tech: ["Vue.js"] },
  ];
  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="portfolio-page">
      <SEOHead title="Portfolio" description="Scopri i progetti realizzati da SIMO Marketing: siti web, app mobile, landing page per aziende italiane e internazionali." />
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff]">I NOSTRI LAVORI</Badge>
          <h1 className="text-3xl font-bold mb-8"><span className="text-[#00f0ff]">Portfolio</span></h1>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
          {projects.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Card className="glass-card overflow-hidden group">
                <div className="relative overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500" />
                  <Badge className="absolute top-4 left-4 bg-[#00f0ff]/20 text-[#00f0ff]">{p.category}</Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold mb-2">{p.title}</h3>
                  <div className="flex gap-2">{p.tech.map((t, j) => <span key={j} className="text-xs px-2 py-1 bg-white/5 rounded">{t}</span>)}</div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

const SEOToolPage = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyzeSEO = async () => {
    if (!url.trim()) return;
    setLoading(true); setResult(null);
    try {
      const response = await axios.post(`${API}/seo/analyze`, { url: url.trim() });
      setResult(response.data);
      // Save lead
      if (response.data.score !== undefined) {
        try {
          await axios.post(`${API}/tool-leads`, {
            tool_type: "seo",
            url: url.trim(),
            result_score: response.data.score
          });
        } catch { }
      }
    } catch { setResult({ error: true }); }
    finally { setLoading(false); }
  };

  const getScoreColor = (score) => score >= 80 ? "#00ff9d" : score >= 60 ? "#ffbd00" : "#ff0055";

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="seo-tool-page">
      <SEOHead title="Analisi SEO Gratuita" description="Analizza gratuitamente la SEO del tuo sito web. Ottieni un punteggio e raccomandazioni per migliorare il posizionamento su Google." />
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff]">STRUMENTO GRATUITO</Badge>
          <h1 className="text-3xl font-bold mb-6">Analisi <span className="text-[#00f0ff]">SEO</span> Gratuita</h1>
          <p className="text-white/70 mb-12 max-w-2xl mx-auto">Inserisci l'URL del tuo sito e scopri come migliorare la SEO</p>
          <div className="max-w-2xl mx-auto flex gap-4">
            <div className="flex-1 relative">
              <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
              <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyPress={(e) => e.key === "Enter" && analyzeSEO()}
                placeholder="https://esempio.com" className="pl-12 h-14 text-lg bg-white/5" data-testid="seo-url-input" />
            </div>
            <Button onClick={analyzeSEO} disabled={loading || !url.trim()} className="h-14 px-8 btn-primary" data-testid="seo-analyze-btn">
              {loading ? <Loader2 className="animate-spin" /> : <><Search size={20} className="mr-2" />Analizza</>}
            </Button>
          </div>
        </div>
      </section>
      {result && !result.error && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-6">
            <Card className="glass-card mb-6">
              <CardContent className="p-8">
                <div className="flex items-center gap-8">
                  <div className="relative">
                    <svg className="w-32 h-32 transform -rotate-90">
                      <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10" />
                      <circle cx="64" cy="64" r="56" fill="none" stroke={getScoreColor(result.score)} strokeWidth="10" strokeLinecap="round"
                        strokeDasharray={`${(result.score / 100) * 352} 352`} className="score-circle" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl font-bold" style={{ color: getScoreColor(result.score) }}>{result.score}</span>
                    </div>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold mb-2">Punteggio SEO</h2>
                    <p className="text-white/60 text-sm mb-4">{result.url}</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge className={result.has_https ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {result.has_https ? <CheckCircle size={14} className="mr-1" /> : <XCircle size={14} className="mr-1" />}HTTPS
                      </Badge>
                      <Badge className={result.has_h1 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {result.has_h1 ? <CheckCircle size={14} className="mr-1" /> : <XCircle size={14} className="mr-1" />}H1
                      </Badge>
                      <Badge className={result.has_viewport ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {result.has_viewport ? <CheckCircle size={14} className="mr-1" /> : <XCircle size={14} className="mr-1" />}Mobile
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardHeader><CardTitle>Raccomandazioni</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {result.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3">
                      {rec.startsWith("✅") ? <CheckCircle size={18} className="text-green-400 flex-shrink-0" /> :
                       rec.startsWith("⚠️") ? <AlertCircle size={18} className="text-yellow-400 flex-shrink-0" /> :
                       rec.startsWith("❌") ? <XCircle size={18} className="text-red-400 flex-shrink-0" /> :
                       <Check size={18} className="text-white/40 flex-shrink-0" />}
                      <span className="text-white/80">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>
      )}
    </div>
  );
};

// ==================== BOOKING CALENDAR COMPONENT ====================
const BookingCalendar = ({ onSuccess }) => {
  const [step, setStep] = useState(1); // 1: select date, 2: select time, 3: fill form
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service_interest: "",
    notes: ""
  });
  const [success, setSuccess] = useState(false);

  // Load available dates on mount
  useEffect(() => {
    loadAvailableDates();
  }, []);

  const loadAvailableDates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings/available-dates`);
      setAvailableDates(response.data.available_dates);
    } catch {
      // Error loading dates
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async (date) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings/available-slots?date=${date}`);
      setAvailableSlots(response.data.available_slots);
    } catch {
      // Error loading slots
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (dateInfo) => {
    setSelectedDate(dateInfo);
    setSelectedTime(null);
    loadAvailableSlots(dateInfo.date);
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        date: selectedDate.date,
        time: selectedTime,
        ...formData
      });
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || "Errore nella prenotazione. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-400" />
        </div>
        <h3 className="text-xl font-bold mb-2">Prenotazione Confermata!</h3>
        <p className="text-white/60 mb-4">
          Ti chiameremo il <span className="text-[#00f0ff] font-semibold">{formatDate(selectedDate.date)}</span> alle <span className="text-[#00f0ff] font-semibold">{selectedTime}</span>
        </p>
        <p className="text-white/40 text-sm">Riceverai un promemoria via email.</p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              step >= s ? 'bg-[#00f0ff] text-black' : 'bg-white/10 text-white/40'
            }`}>
              {s}
            </div>
            {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-[#00f0ff]' : 'bg-white/10'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Select Date */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Calendar className="text-[#00f0ff]" size={20} />
            Scegli una Data
          </h3>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-[#00f0ff]" size={32} />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-64 overflow-y-auto pr-2">
              {availableDates.map((dateInfo) => (
                <motion.button
                  key={dateInfo.date}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDateSelect(dateInfo)}
                  className="p-3 bg-white/5 hover:bg-[#00f0ff]/10 border border-white/10 hover:border-[#00f0ff]/50 rounded-lg text-left transition-all"
                >
                  <div className="text-sm font-semibold">{formatDate(dateInfo.date)}</div>
                  <div className="text-xs text-white/60">{dateInfo.day_name}</div>
                  <div className="text-xs text-[#00f0ff] mt-1">{dateInfo.available_slots} slot disponibili</div>
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Step 2: Select Time */}
      {step === 2 && (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          <button onClick={() => setStep(1)} className="flex items-center gap-1 text-white/60 hover:text-white mb-4 text-sm">
            <ChevronLeft size={16} /> Cambia data
          </button>
          <h3 className="font-bold mb-2 flex items-center gap-2">
            <Clock className="text-[#00f0ff]" size={20} />
            Scegli un Orario
          </h3>
          <p className="text-white/60 text-sm mb-4">
            {selectedDate.day_name}, {formatDate(selectedDate.date)}
          </p>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-[#00f0ff]" size={32} />
            </div>
          ) : availableSlots.length === 0 ? (
            <p className="text-white/60 text-center py-4">Nessun orario disponibile per questa data</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-2">
              {availableSlots.map((time) => (
                <motion.button
                  key={time}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleTimeSelect(time)}
                  className="py-2 px-3 bg-white/5 hover:bg-[#00f0ff]/20 border border-white/10 hover:border-[#00f0ff] rounded-lg text-sm font-medium transition-all"
                >
                  {time}
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Step 3: Fill Form */}
      {step === 3 && (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          <button onClick={() => setStep(2)} className="flex items-center gap-1 text-white/60 hover:text-white mb-4 text-sm">
            <ChevronLeft size={16} /> Cambia orario
          </button>
          <div className="bg-[#00f0ff]/10 border border-[#00f0ff]/30 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-3">
              <PhoneCall className="text-[#00f0ff]" size={20} />
              <div>
                <div className="font-semibold">{selectedDate.day_name}, {formatDate(selectedDate.date)}</div>
                <div className="text-[#00f0ff]">Ore {selectedTime}</div>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm mb-1">Nome Completo *</label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10"
                placeholder="Mario Rossi"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1">Telefono *</label>
                <Input
                  required
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white/5 border-white/10"
                  placeholder="+39 333 1234567"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Email *</label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/5 border-white/10"
                  placeholder="email@esempio.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm mb-1">Servizio di Interesse *</label>
              <Select value={formData.service_interest} onValueChange={(v) => setFormData({ ...formData, service_interest: v })}>
                <SelectTrigger className="bg-white/5 border-white/10">
                  <SelectValue placeholder="Seleziona un servizio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="landing">Landing Page (€200)</SelectItem>
                  <SelectItem value="sito_vetrina">Sito Vetrina (da €800)</SelectItem>
                  <SelectItem value="sito_aziendale">Sito Aziendale (da €2.000)</SelectItem>
                  <SelectItem value="ecommerce">E-commerce (da €3.000)</SelectItem>
                  <SelectItem value="app">App Mobile (da €5.000)</SelectItem>
                  <SelectItem value="seo">SEO (da €300/mese)</SelectItem>
                  <SelectItem value="social">Social Media (da €400/mese)</SelectItem>
                  <SelectItem value="google_ads">Google Ads</SelectItem>
                  <SelectItem value="branding">Branding / Logo</SelectItem>
                  <SelectItem value="altro">Altro / Non so ancora</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm mb-1">Note (opzionale)</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-white/5 border-white/10"
                placeholder="Descrivi brevemente il tuo progetto o le tue esigenze..."
                rows={3}
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !formData.service_interest}
              className="w-full btn-primary py-6"
            >
              {submitting ? <Loader2 className="animate-spin mr-2" /> : <PhoneCall className="mr-2" size={18} />}
              Conferma Prenotazione
            </Button>
            <p className="text-center text-white/40 text-xs">
              Ti chiameremo all'orario selezionato. Durata: ~30 minuti.
            </p>
          </form>
        </motion.div>
      )}
    </div>
  );
};

const ConsulenzaPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", service_type: "", budget: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showBooking, setShowBooking] = useState(true); // Default to booking calendar

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await axios.post(`${API}/consultations`, formData);
      setSuccess(true);
    } catch { alert("Errore"); }
    finally { setLoading(false); }
  };

  if (success) return (
    <div className="min-h-screen pt-28 md:pt-32 flex items-center justify-center" data-testid="consulenza-success">
      <div className="text-center">
        <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-4">Richiesta Inviata!</h2>
        <p className="text-white/60 mb-8">Ti contatteremo entro 24 ore.</p>
        <Link to="/"><Button className="btn-primary">Torna alla Home</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="consulenza-page">
      <SEOHead title="Consulenza Gratuita" description="Richiedi una consulenza gratuita con SIMO Marketing. Prenota una chiamata o compila il form." />
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff]">GRATUITA</Badge>
            <h1 className="text-3xl font-bold mb-4">Richiedi una <span className="text-[#00f0ff]">Consulenza</span></h1>
            <p className="text-white/60">Scegli come preferisci essere contattato</p>
          </div>

          {/* Toggle Buttons */}
          <div className="flex justify-center gap-4 mb-8">
            <Button
              variant={showBooking ? "default" : "outline"}
              onClick={() => setShowBooking(true)}
              className={showBooking ? "btn-primary" : "btn-secondary"}
            >
              <PhoneCall size={18} className="mr-2" />
              Prenota Chiamata
            </Button>
            <Button
              variant={!showBooking ? "default" : "outline"}
              onClick={() => setShowBooking(false)}
              className={!showBooking ? "btn-primary" : "btn-secondary"}
            >
              <Mail size={18} className="mr-2" />
              Compila Form
            </Button>
          </div>

          {showBooking ? (
            /* Booking Calendar */
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="text-[#00f0ff]" />
                  Prenota una Chiamata
                </CardTitle>
                <p className="text-white/60 text-sm">Scegli data e orario e ti chiameremo noi. Orari: Lun-Sab, 9:00-19:00</p>
              </CardHeader>
              <CardContent>
                <BookingCalendar />
              </CardContent>
            </Card>
          ) : (
            /* Traditional Form */
            <Card className="glass-card">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div><label className="block text-sm mb-2">Nome *</label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white/5" /></div>
                    <div><label className="block text-sm mb-2">Email *</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white/5" /></div>
                    <div><label className="block text-sm mb-2">Telefono *</label><Input required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-white/5" /></div>
                    <div><label className="block text-sm mb-2">Azienda</label><Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="bg-white/5" /></div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div><label className="block text-sm mb-2">Servizio *</label>
                      <Select value={formData.service_type} onValueChange={(v) => setFormData({ ...formData, service_type: v })}>
                        <SelectTrigger className="bg-white/5"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="landing">Landing Page (€200)</SelectItem>
                          <SelectItem value="web">Sito Web</SelectItem>
                          <SelectItem value="app">App Mobile</SelectItem>
                          <SelectItem value="seo">SEO</SelectItem>
                          <SelectItem value="social">Social Media</SelectItem>
                          <SelectItem value="altro">Altro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><label className="block text-sm mb-2">Budget</label>
                      <Select value={formData.budget} onValueChange={(v) => setFormData({ ...formData, budget: v })}>
                        <SelectTrigger className="bg-white/5"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="200-500">€200 - €500</SelectItem>
                          <SelectItem value="500-1000">€500 - €1.000</SelectItem>
                          <SelectItem value="1000-3000">€1.000 - €3.000</SelectItem>
                          <SelectItem value="3000+">€3.000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><label className="block text-sm mb-2">Descrizione *</label><Textarea required rows={4} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-white/5" /></div>
                  <Button type="submit" disabled={loading} className="w-full btn-primary py-6">{loading ? <Loader2 className="animate-spin mr-2" /> : null}Invia Richiesta</Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
};

const ContattiPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("booking"); // booking or form

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await axios.post(`${API}/contacts`, formData); setSuccess(true); } catch { alert("Errore"); }
    finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen pt-28 md:pt-32" data-testid="contatti-page">
      <SEOHead title="Contatti" description="Contatta SIMO Marketing: Viale Regina Margherita, Caltanissetta. Tel: 351 344 9302. Email: info@simomarketing.it" />
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#00f0ff]/10 text-[#00f0ff]">CONTATTACI</Badge>
            <h1 className="text-3xl font-bold">Parliamo del tuo <span className="text-[#00f0ff]">progetto</span></h1>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-4">
              {[
                { icon: MapPin, title: "Sede", text: "Viale Regina Margherita, Caltanissetta", sub: "Operiamo in tutta Italia" },
                { icon: Phone, title: "Telefono", text: "+39 351 344 9302", link: "tel:+393513449302" },
                { icon: Mail, title: "Email", text: "info@simomarketing.it", link: "mailto:info@simomarketing.it" },
                { icon: Clock, title: "Orari", text: "Lun - Sab: 9:00 - 19:00", sub: "Domenica: Chiuso" },
              ].map((item, i) => (
                <Card key={i} className="glass-card"><CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#00f0ff]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-[#00f0ff]" size={18} />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">{item.title}</h3>
                    {item.link ? (
                      <a href={item.link} className="text-[#00f0ff] hover:underline text-sm">{item.text}</a>
                    ) : (
                      <p className="text-white/60 text-sm">{item.text}</p>
                    )}
                    {item.sub && <p className="text-white/40 text-xs">{item.sub}</p>}
                  </div>
                </CardContent></Card>
              ))}
            </div>

            {/* Main Content - Booking or Form */}
            <div className="lg:col-span-2">
              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full bg-white/5">
                      <TabsTrigger value="booking" className="flex-1">
                        <PhoneCall size={16} className="mr-2" />
                        Prenota Chiamata
                      </TabsTrigger>
                      <TabsTrigger value="form" className="flex-1">
                        <Mail size={16} className="mr-2" />
                        Invia Messaggio
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </CardHeader>
                <CardContent className="p-6">
                  {activeTab === "booking" ? (
                    <div>
                      <p className="text-white/60 text-sm mb-4">
                        Scegli data e orario e ti chiameremo noi per parlare del tuo progetto.
                      </p>
                      <BookingCalendar />
                    </div>
                  ) : (
                    <>
                      {success ? (
                        <div className="text-center py-8">
                          <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
                          <h3 className="text-xl font-bold">Messaggio Inviato!</h3>
                          <p className="text-white/60 mt-2">Ti risponderemo al più presto.</p>
                        </div>
                      ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div><label className="block text-sm mb-2">Nome *</label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white/5" /></div>
                            <div><label className="block text-sm mb-2">Email *</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white/5" /></div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div><label className="block text-sm mb-2">Telefono</label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-white/5" /></div>
                            <div><label className="block text-sm mb-2">Oggetto *</label><Input required value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="bg-white/5" /></div>
                          </div>
                          <div><label className="block text-sm mb-2">Messaggio *</label><Textarea required rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="bg-white/5" /></div>
                          <Button type="submit" disabled={loading} className="w-full btn-primary py-6">{loading ? <Loader2 className="animate-spin mr-2" /> : null}Invia Messaggio</Button>
                        </form>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== MAIN APP ====================
function App() {
  return (
    <div className="App min-h-screen bg-[#050a14] overflow-x-hidden">
      <BrowserRouter>
        <ScrollToTop />
        <ParticlesBackground />
        <Navbar />
        <main className="relative z-10 overflow-x-hidden">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/chi-siamo" element={<ChiSiamoPage />} />
            <Route path="/servizi" element={<ServiziPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/tools/seo" element={<SEOToolPage />} />
            <Route path="/tools/social" element={<SocialToolPage />} />
            <Route path="/tools/rank" element={<RankCheckerPage />} />
            <Route path="/tools/speed" element={<SpeedTestPage />} />
            <Route path="/tools/ssl" element={<SSLCheckerPage />} />
            <Route path="/tools/mobile" element={<MobileTestPage />} />
            <Route path="/tools/security" element={<SecurityHeadersPage />} />
            <Route path="/tools/cyber" element={<CyberSecurityPage />} />
            <Route path="/seo-tool" element={<SEOToolPage />} />
            <Route path="/social-tool" element={<SocialToolPage />} />
            <Route path="/consulenza" element={<ConsulenzaPage />} />
            <Route path="/contatti" element={<ContattiPage />} />
            <Route path="/accesso" element={<AccessPage />} />
            <Route path="/login-cliente" element={<ClientLoginPage />} />
            <Route path="/login-admin" element={<AdminLoginPage />} />
            <Route path="/dashboard-cliente" element={<ClientDashboard />} />
            <Route path="/dashboard-admin" element={<AdminDashboard />} />
          </Routes>
        </main>
        <Footer />
        <Chatbot />
      </BrowserRouter>
    </div>
  );
}

export default App;
